import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { 
  MoreHorizontal, 
  Eye, 
  Edit, 
  Trash2, 
  UserCheck, 
  UserX, 
  CreditCard,
  Search,
  Filter,
  Plus
} from 'lucide-react';
import { getAdminUserList, updateUserRole, updateUserSubscriptionStatus, deleteUser } from '@/api/admin';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  subscription_status: string;
  is_banned: boolean;
  created_at: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  plan_id?: string;
  price_id?: string;
  subscription_id?: string;
}

interface UserTableProps {
  onUserSelect?: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({ onUserSelect }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch users
  const { data: usersData = [], isLoading, error } = useQuery({
    queryKey: ['adminUserList'],
    queryFn: getAdminUserList,
  });

  // Fetch plans for real pricing data
  const { data: plans = [] } = useQuery({
    queryKey: ['adminPlans'],
    queryFn: async () => {
      const { getPlans } = await import('@/api/admin');
      return await getPlans();
    },
  });

  // Define Plan interface for type safety
  interface Plan {
    id: string;
    name: string;
    price: number;
    is_active: boolean;
  }

  // Type guard to check if data is valid user array
  const isValidUser = (item: any): item is User => {
    return item && typeof item === 'object' && 
           'id' in item && 
           'email' in item && 
           'name' in item &&
           !('error' in item); // Exclude error objects
  };

  // Filter out invalid data and only process valid users
  const users = React.useMemo(() => {
    if (!Array.isArray(usersData)) {
      console.warn('Invalid users data received:', usersData);
      return [];
    }
    return usersData.filter(isValidUser);
  }, [usersData]);

  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'user' | 'admin' }) =>
      updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
      toast({
        title: "Success",
        description: "User role updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user role",
        variant: "destructive",
      });
    },
  });

  // Update subscription status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: string }) =>
      updateUserSubscriptionStatus(userId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
      toast({
        title: "Success",
        description: "User subscription status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update subscription status",
        variant: "destructive",
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
      setDeleteUserId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
      setDeleteUserId(null);
    },
  });

  // Filter users based on search and filters
  const filteredUsers = users.filter((user: User) => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.subscription_status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  // Calculate metrics from user data based on actual plans
  const metrics = React.useMemo(() => {
    // Filter plans to only include valid plan objects (not database errors)
    const validPlans = plans.filter((p: any) => 
      p && typeof p === 'object' && 
      'name' in p && 
      'price' in p && 
      'is_active' in p &&
      !('error' in p)
    ) as Plan[];
    
    // Create a map of plan IDs to plan details for quick lookup
    const planMap = new Map(validPlans.map(p => [p.id, p]));
    
    // Only count users who have an actual plan assigned (not just subscription status)
    const paidUsers = users.filter(user => {
      // User must have an active/trialing subscription AND be linked to a plan
      return ['active', 'trialing'].includes(user.subscription_status || '') && 
             user.plan_id && 
             planMap.has(user.plan_id);
    });
    
    // Users with declined/failed payments but still linked to plans
    const declinedUsers = users.filter(user => 
      ['past_due', 'canceled', 'unpaid'].includes(user.subscription_status || '') &&
      user.plan_id &&
      planMap.has(user.plan_id)
    );
    
    // Calculate real revenue based on actual plan prices
    const calculateUserRevenue = (user: User) => {
      if (!user.plan_id) return 0;
      
      const plan = planMap.get(user.plan_id);
      if (!plan) return 0;
      
      // Convert price to number (it comes as string from database)
      return parseFloat(plan.price.toString()) || 0;
    };
    
    const amountPaid = paidUsers.reduce((total, user) => total + calculateUserRevenue(user), 0);
    const soonToPay = paidUsers.reduce((total, user) => total + calculateUserRevenue(user), 0); // Same as paid for monthly subscriptions
    const declined = declinedUsers.reduce((total, user) => total + calculateUserRevenue(user), 0);
    
    return {
      totalUsers: users.length,
      paidUsers: paidUsers.length,
      amountPaid,
      soonToPay,
      declined,
      declinedCount: declinedUsers.length
    };
  }, [users, plans]);

  const handleRowClick = (user: User) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleViewUser = (user: User) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleEditUser = (user: User) => {
    navigate(`/admin/users/${user.id}/edit`);
  };

  const handleDeleteUser = (userId: string) => {
    // Find the user to check subscription status
    const user = users.find(u => u.id === userId);
    if (!user) {
      toast({
        title: "Error",
        description: "User not found",
        variant: "destructive",
      });
      return;
    }

    // Check if user has an active subscription
    const hasActiveSubscription = user.subscription_status === 'active' || user.subscription_status === 'trialing';
    if (hasActiveSubscription) {
      toast({
        title: "Cannot Delete User",
        description: "This user has an active subscription. Please cancel their subscription first before deleting the account.",
        variant: "destructive",
      });
      return;
    }

    // Proceed with deletion if no active subscription
    setDeleteUserId(userId);
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserId) return;
    
    deleteUserMutation.mutate(deleteUserId);
  };

  const handleRoleChange = (userId: string, newRole: 'user' | 'admin') => {
    updateRoleMutation.mutate({ userId, role: newRole });
  };

  const handleStatusChange = (userId: string, newStatus: string) => {
    updateStatusMutation.mutate({ userId, status: newStatus });
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      trialing: 'bg-blue-100 text-blue-800',
    };
    
    return (
      <Badge className={statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}>
        {status || 'none'}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    return (
      <Badge variant={role === 'admin' ? 'destructive' : 'secondary'}>
        {role}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2">Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-600">Error loading users: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header with Stats */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">User Management</h2>

            <p className="text-muted-foreground text-gray-600">Manage users, roles, and subscriptions- Showing {filteredUsers.length} of {users.length} users</p>
          
          </div>
        </div>
        <Button onClick={() => navigate('/admin/users/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Users</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.totalUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Paid Users</p>
              <p className="text-2xl font-bold text-green-600">{metrics.paidUsers}</p>
              <p className="text-xs text-muted-foreground">${metrics.amountPaid}/month</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Eye className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Soon to Pay</p>
              <p className="text-2xl font-bold text-orange-600">${metrics.soonToPay}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <UserX className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Declined</p>
              <p className="text-2xl font-bold text-red-600">${metrics.declined}</p>
              <p className="text-xs text-muted-foreground">{metrics.declinedCount} users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Search and Filter Controls */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between p-4 bg-muted/50 rounded-lg border">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email, name, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-background"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border rounded-md bg-background text-sm min-w-[100px]"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md bg-background text-sm min-w-[120px]"
            >
              <option value="all">All Status</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="pro">Pro</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>Filters active: {[roleFilter !== 'all' ? 'Role' : null, statusFilter !== 'all' ? 'Status' : null, searchTerm ? 'Search' : null].filter(Boolean).length}</span>
        </div>
      </div>

      {/* Enhanced Users table */}
      <div className="border rounded-lg bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">User</TableHead>
              <TableHead className="font-semibold">Role</TableHead>
              <TableHead className="font-semibold">Subscription</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Created</TableHead>
              <TableHead className="font-semibold">Stripe</TableHead>
              <TableHead className="text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="rounded-full bg-muted p-3">
                      <Search className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium">No users found</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' 
                          ? 'Try adjusting your filters'
                          : 'No users have been added yet'}
                      </p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user: User) => (
                <TableRow 
                  key={user.id} 
                  className="hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(user)}
                >
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">{user.name || 'No name'}</span>
                        <span className="text-sm text-muted-foreground truncate">{user.email}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">{getRoleBadge(user.role)}</TableCell>
                  <TableCell className="py-4">{getStatusBadge(user.subscription_status)}</TableCell>
                  <TableCell className="py-4">
                    <Badge variant={user.is_banned ? 'destructive' : 'secondary'}>
                      {user.is_banned ? 'Banned' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-4 text-sm text-muted-foreground">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell className="py-4">
                    {user.stripe_customer_id ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CreditCard className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                        No Stripe
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          className="h-8 w-8 p-0 hover:bg-muted"
                          aria-label="Open user actions menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleViewUser(user);
                        }}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(user);
                        }}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRoleChange(user.id, user.role === 'admin' ? 'user' : 'admin');
                          }}
                        >
                          {user.role === 'admin' ? (
                            <>
                              <UserX className="h-4 w-4 mr-2" />
                              Remove Admin
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-2" />
                              Make Admin
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUser(user.id);
                          }}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {filteredUsers.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                const isActive = pageNum === currentPage;
                return (
                  <Button
                    key={pageNum}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-9 h-9"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              {totalPages > 5 && (
                <>
                  {currentPage < totalPages - 2 && <span className="px-2">...</span>}
                  {currentPage < totalPages - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      className="w-9 h-9"
                    >
                      {totalPages}
                    </Button>
                  )}
                </>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user
              and remove all their data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUser}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
